import { useCallback, useMemo } from 'react';
import * as filestack from 'filestack-js';
import { v6 as uuidv6 } from 'uuid';
import { DEFAULT_FORMATS } from './constants';
import { sanitizeFiles } from './utils';
import { UploadedAsset, SanitizedFileResult, UseMultiAssetUploaderProps, UseMultiAssetUploaderReturn } from './types';
import { UPLOAD_CONFIG } from '@/Pages/BrandKit/components/BrandTone/constants';
import { getContentStoreConfig } from '@/Pages/BrandKit/components/BrandTone/utils';
import { getEnvConfig } from '@/constants';
import { useAppContext } from '@/context/AppContext/AppContext';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
import { uploadFileWithFilestackSecurityRetry } from '@/utils/filestackSecurity';

/**
 * Custom hook for handling multi-asset uploads with validation and error handling
 *
 * @param props Configuration for the upload behavior
 * @returns Upload utilities and state
 */
export const useMultiAssetUploader = ({
  baseFilePath,
  assetPath,
  formats = DEFAULT_FORMATS,
  maxSize = 20 * 1024 * 1024,
  maxIndividualFileSize,
  handleUploadedAssets,
  onError
}: UseMultiAssetUploaderProps): UseMultiAssetUploaderReturn => {
  const { logger } = useAppContext();

  // Initialize FileStack client
  const client = useMemo(() => filestack.init(getEnvConfig('FILESTACK_API_KEY')), []);

  // Format accepted file formats for input elements
  const acceptedFormats = useMemo(() => formats.map(format => `.${format}`).join(','), [formats]);

  // Validate files without uploading
  const validateFiles = useCallback(
    async (files: File[]): Promise<SanitizedFileResult> => {
      return await sanitizeFiles(files, formats, maxSize, maxIndividualFileSize);
    },
    [formats, maxSize, maxIndividualFileSize]
  );

  // Core upload functionality
  const uploadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      if (!files.length) {
        return;
      }

      try {
        const { validFiles, invalidFiles } = await sanitizeFiles(files, formats, maxSize, maxIndividualFileSize);

        const uploadedAssets: UploadedAsset[] = [];

        for (const { file, type, duration } of validFiles) {
          try {
            const fileId: string = uuidv6({ msecs: new Date().getTime() });

            const storeConfig = getContentStoreConfig(assetPath, `${fileId}_original`, baseFilePath);
            const response = await uploadFileWithFilestackSecurityRetry({
              client,
              file,
              uploadOptions: UPLOAD_CONFIG,
              storeOptions: storeConfig,
              filestackIntent: { intent: 'clip_overlay_asset', assetKind: type },
              isSignedFilestackUploadsEnabled: Boolean(
                featureFlagStore.getSnapshot()[FeatureFlagKeys.Use_Signed_Filestack_Uploads]
              )
            });

            const resourceKey = encodeURIComponent(response?.key ?? '');
            const url = `${getEnvConfig('STATIC_ASSETS_CDN')}/${resourceKey}`;

            uploadedAssets.push({
              url,
              type,
              filename: file.name,
              duration
            });
          } catch (fileError) {
            logger.error(`Error uploading ${file.name}:`, fileError);
            onError?.({
              type: 'uploadError',
              message: `Error uploading ${file.name}`
            });
          }
        }

        handleUploadedAssets({
          validFiles: uploadedAssets,
          invalidFiles
        });
      } catch (error) {
        logger.error('Error during bulk upload:', error);
        onError?.({
          type: 'error',
          message: 'Error during bulk upload'
        });
      }
    },
    [client, baseFilePath, assetPath, handleUploadedAssets, formats, maxSize, maxIndividualFileSize, onError, logger]
  );

  return {
    uploadFiles,
    validateFiles,
    acceptedFormats
  };
};
